import { Input, Button, Popover, Skeleton, Badge, Divider } from "antd"
import ConversationItem from "../../components/Chat/ConversationItem"
import QuickFilterRail, { QuickFilterKey } from "./QuickFilterRail";
import { useEffect, useState } from "react";
import { SearchOutlined, CloseCircleOutlined, FilterOutlined, UsergroupAddOutlined, PushpinOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fetchCareStatuses, mapCareStatusToLabel } from "./chatLabels";
import { Pin } from "lucide-react";

interface ConversationListProps {
    data: any[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    channelName?: string;
    selectedCareStatusId?: number;
    onFilterByCareStatus?: (careStatusId?: number) => void;
    selectedType?: string;
    onFilterByType?: (type?: string) => void;
    onLoadMore?: () => void;
    onSelect: (item: any) => void;
    activeChat: any;
    onSearch?: (value: string) => void;
    /** Đổi bộ lọc nhanh ở thanh icon bên trái */
    onQuickFilterChange?: (key: QuickFilterKey) => void;
    /** Bỏ hết tìm kiếm và bộ lọc, nạp lại danh sách một lần */
    onResetFilters?: () => void;
}

export default function ConversationList({ data, loading, loadingMore, hasMore, channelName, selectedCareStatusId, onFilterByCareStatus, selectedType, onFilterByType, onLoadMore, onSelect, activeChat, onSearch, onQuickFilterChange, onResetFilters }: ConversationListProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [careLabels, setCareLabels] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState("");
    // Icon lọc nhanh đang chọn ở thanh bên trái
    const [quickFilter, setQuickFilter] = useState<QuickFilterKey>("all");

    const selectedTags =
        selectedCareStatusId !== undefined
            ? [selectedCareStatusId]
            : [];
    const hasActiveTagFilter = selectedCareStatusId !== undefined;
    // Có bất kỳ bộ lọc nào đang áp dụng (tag hoặc loại) -> hiện dấu hiệu trên nút Tag
    const hasActiveFilter = hasActiveTagFilter || selectedType !== undefined;

    const handleClearTagFilter = () => {
        onFilterByCareStatus?.(undefined);
    };

    const handleToggleTag = (key: string) => {
        const careStatusId = Number(key);
        // Single-select: toggle off if already selected, otherwise select
        if (selectedCareStatusId === careStatusId) {
            onFilterByCareStatus?.(undefined);
        } else {
            onFilterByCareStatus?.(careStatusId);
        }
    };

    useEffect(() => {
        fetchCareStatuses().then((items) => {
            setCareLabels(items.map(mapCareStatusToLabel));
        });
    }, []);

    // Có tìm kiếm / bộ lọc nào đang áp dụng -> danh sách rỗng thì gợi ý bỏ lọc
    const isFiltered = !!searchValue.trim() || hasActiveFilter || quickFilter !== "all";

    const handleResetFilters = () => {
        setSearchValue("");
        setQuickFilter("all");
        onResetFilters?.();
    };

    const tagContent = (
        // Danh sách thẻ dài hơn màn hình: giới hạn chiều cao và cho cuộn bên trong
        <div className="w-[220px] text-000000e0 overflow-y-auto overscroll-contain pr-1" style={{ maxHeight: "calc(100vh - 160px)" }}>
            {/* Menu Top */}
            <div className="space-y-1">
                <div
                    onClick={() => {
                        onFilterByType?.(undefined);
                        onFilterByCareStatus?.(undefined);
                    }}
                    className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer ${!hasActiveFilter
                        ? "bg-[#3e91ab]/10 text-[#3e91ab] font-medium"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                >
                    <span>{t("roomchat.filter_all_customers")}</span>
                    <UsergroupAddOutlined className={!hasActiveFilter ? "text-[#3e91ab]" : "text-gray-400"} />
                </div>

                {/* Lọc "Chưa đọc" nằm ở thanh icon bên trái (is_unread), không lặp lại ở đây */}

                {/* Tạm ẩn bộ lọc "Đã ghim" (sẽ mở lại sau)
                <div
                    className="flex items-center justify-between px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                    <span>Đã ghim</span>
                    <Pin
                        size={14}
                        className="text-[#3e91ab] flex-shrink-0"
                        fill="#3e91ab"
                    />
                </div>
                */}
            </div>

            <Divider className="my-2" />

            {/* Lọc theo loại hội thoại: Chat / Bình luận */}
            <div className="px-2 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t("roomchat.filter_by_type")}
                </span>
            </div>
            <div className="space-y-1 mb-1">
                {[
                    { label: t("roomchat.type_chat"), value: "chat" },
                    { label: t("roomchat.filter_comment"), value: "comment" },
                ].map((opt) => {
                    const active = (selectedType ?? undefined) === opt.value;
                    return (
                        <div
                            key={opt.label}
                            onClick={() => onFilterByType?.(opt.value)}
                            className={`flex items-center justify-between px-2 py-1.5 rounded cursor-pointer ${active
                                ? "bg-[#3e91ab]/10 text-[#3e91ab] font-medium"
                                : "hover:bg-gray-100 dark:hover:bg-gray-700"
                                }`}
                        >
                            <span>{opt.label}</span>
                            {active && <CheckOutlined className="text-[#3e91ab]" />}
                        </div>
                    );
                })}
            </div>

            <Divider className="my-2" />

            {/* Header */}
            <div className="flex items-center justify-between px-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t("roomchat.filter_by_tag")}
                </span>

                {hasActiveTagFilter && (
                    <CloseCircleOutlined
                        onClick={handleClearTagFilter}
                        className="cursor-pointer text-red-500"
                    />
                )}
            </div>

            {/* Tags */}
            <div className="space-y-1">
                {careLabels.map((label) => {
                    const active = selectedTags.includes(label.id);

                    return (
                        <div
                            key={label.id}
                            onClick={() => handleToggleTag(label.id)}
                            className={`
                                flex items-center gap-3
                                px-2 py-1.5
                                rounded
                                cursor-pointer
                                transition
                                hover:bg-gray-100
                                dark:hover:bg-gray-700
                                ${active ? "bg-gray-100 dark:bg-gray-700" : ""}
                            `}
                        >
                            <span
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{
                                    backgroundColor: label.color,
                                }}
                            />

                            <span className="flex-1 text-sm">
                                {label.label}
                            </span>

                            {active && (
                                <CheckOutlined className="text-[#3e91ab]" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );


    // Lọc nhanh bằng thanh icon bên trái. Mục nào API chưa hỗ trợ thì báo cho
    // người dùng biết thay vì bấm xong không có gì xảy ra.
    const handleQuickFilter = (key: QuickFilterKey) => {
        setQuickFilter(key);
        if (key === "all") onFilterByCareStatus?.(undefined);
        onQuickFilterChange?.(key);
    };

    return (
        <div className="w-full md:w-[360px] border-r dark:border-[#2a2a2a] flex h-full">

            {/* Thanh icon lọc nhanh */}
            <QuickFilterRail value={quickFilter} onChange={handleQuickFilter} />

            <div className="flex-1 min-w-0 flex flex-col h-full">

            <div className="flex gap-2 p-3">

                {/* Search input */}
                <Input
                    placeholder={t("roomchat.search")}
                    prefix={<SearchOutlined />}
                    className="flex-1"
                    value={searchValue}
                    onChange={(e) => {
                        setSearchValue(e.target.value);
                        onSearch?.(e.target.value);
                    }}
                />

                {/* Tag button */}
                <Popover
                    content={tagContent}
                    trigger="click"
                    open={open}
                    onOpenChange={setOpen}
                    placement="bottomRight"
                >
                    <Badge dot={hasActiveFilter} color="#10b981">
                        <Button
                            icon={<FilterOutlined />}
                            type={hasActiveFilter ? "primary" : "default"}
                        >
                            Tag
                        </Button>
                    </Badge>
                </Popover>

            </div>

            <div
                className="flex-1 min-h-0 overflow-y-auto"
                onScroll={(e) => {
                    if (loading || loadingMore || !hasMore || !onLoadMore) return;
                    const el = e.currentTarget;
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
                        onLoadMore();
                    }
                }}
            >
                {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                        <div
                            key={`skeleton-${idx}`}
                            className="flex items-center gap-3 px-3 py-3 border-b border-color-1f1f1f"
                        >
                            <Skeleton.Avatar active size={48} shape="circle" />
                            <div className="flex-1">
                                <Skeleton active title={false} paragraph={{ rows: 2, width: ['70%', '90%'] }} />
                            </div>
                        </div>
                    ))
                ) : !data.length ? (
                    <div className="px-6 py-12 flex flex-col items-center text-center gap-2 text-gray-500 dark:text-gray-400">
                        <SearchOutlined className="text-2xl text-gray-300" />
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {t(isFiltered ? "roomchat.empty_filtered" : "roomchat.empty_list")}
                        </div>
                        {isFiltered && (
                            <Button size="small" onClick={handleResetFilters}>
                                {t("roomchat.clear_filters")}
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        {data.map((item) => (
                            <ConversationItem
                                key={item.id}
                                data={item}
                                active={activeChat?.id === item.id}
                                onClick={() => onSelect(item)}
                            />
                        ))}
                        {loadingMore && (
                            Array.from({ length: 3 }).map((_, idx) => (
                                <div
                                    key={`skeleton-more-${idx}`}
                                    className="flex items-center gap-3 px-3 py-3 border-b border-color-1f1f1f"
                                >
                                    <Skeleton.Avatar active size={48} shape="circle" />
                                    <div className="flex-1">
                                        <Skeleton active title={false} paragraph={{ rows: 2, width: ['70%', '90%'] }} />
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>

            </div>

        </div>
    )
}